import { Component, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { APP_LOGO_URL, APP_NAME } from '@admin-core/constants/app.constants';
import { AuthService } from '@admin-core/services/auth.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { TranslationKey } from '@admin-core/i18n/translations/en';
import { LanguageSwitcherComponent } from '@admin-shared/components/language-switcher/language-switcher.component';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, TranslatePipe, LanguageSwitcherComponent],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent {
  readonly appName = APP_NAME;
  readonly logoUrl = APP_LOGO_URL;
  readonly submitting = signal(false);
  readonly done = signal(false);
  readonly error = signal<TranslationKey | null>(null);
  readonly tokenMissing = signal(false);
  readonly form;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly auth: AuthService
  ) {
    const token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';
    this.tokenMissing.set(!token);

    this.form = this.fb.nonNullable.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    });
  }

  submit(): void {
    if (this.tokenMissing()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { password, confirmPassword } = this.form.getRawValue();
    if (password !== confirmPassword) {
      this.error.set('passwordReset.passwordMismatch');
      return;
    }

    const token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';
    this.submitting.set(true);
    this.error.set(null);

    this.auth.resetPassword(token, password).subscribe({
      next: () => {
        this.submitting.set(false);
        this.done.set(true);
      },
      error: () => {
        this.submitting.set(false);
        this.error.set('passwordReset.resetError');
      }
    });
  }

  goToLogin(): void {
    void this.router.navigateByUrl('/login');
  }
}
